import { t } from "i18next";
import { useAtom } from "jotai";
import { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { logger } from "#app/monitoring/Logger";
import { defaultFiltersParams } from "#app/routes/pages/Search.page";
import { useSearchParts } from "#modules/parts/view/useSearchParts";
import { useGetQueryParams } from "#modules/quoteRequestLines/infra/useGetQueryParams";
import { useHandleSetFilterParams } from "#modules/search/infra/useHandleSetFilterParams";
import { SearchingStatusCheck } from "#modules/similarPart/view/errors/SearchingStatusCheck";
import { SimilarPartsErrorCheck } from "#modules/similarPart/view/errors/SimilarPartsErrorCheck";
import { ResultPartCard } from "#modules/similarPart/view/ResultPartCard";
import { ResultToolbar } from "#modules/similarPart/view/ResultToolbar";
import { SearchResult } from "#modules/similarPart/view/SearchResult";
import { useDataChunkSize } from "#shared/helpers/useDataChunkSize";
import { PageLayout } from "#shared/layouts/PageLayout";
import { filtersParamsAtom } from "#shared/view/components/AppShell/Atoms";
import { Group } from "#shared/view/components/Group";
import { Space } from "#shared/view/components/Space";
import { Stack } from "#shared/view/components/Stack";

export const ManualSearchPage = () => {
  const { state } = useLocation();

  const { filters, columns, limit } = useGetQueryParams({
    params: ["filters", "columns", "limit"],
  });

  const { dataChunkSize } = useDataChunkSize(limit ? parseInt(limit) : 10);
  const [filtersParams, setFiltersParams] = useAtom(filtersParamsAtom);

  filtersParamsAtom.onMount = (setAtom) => {
    setAtom(state?.filtersParams);
  };

  const { handleSetFilterParams, DEFAULT_COLUMNS_COUNT } = useHandleSetFilterParams({
    setFiltersParams,
  });

  useEffect(() => {
    if (filters) {
      try {
        const decodedFilters = JSON.parse(decodeURIComponent(filters));
        setFiltersParams(decodedFilters);
      } catch (e: unknown) {
        if (e instanceof Error) {
          logger.error(`Erreur lors du décodage des filtres: ${e.message}`);
        }
      }
    } else {
      setFiltersParams(defaultFiltersParams);
    }
  }, [setFiltersParams, filters]);

  const { parts, refetch, isLoading } = useSearchParts({
    filtersParams,
    limit: dataChunkSize,
  });

  const Card = useCallback(
    (props: Parameters<typeof ResultPartCard>[0]) => (
      <ResultPartCard {...props} featuresDisplayMode="none" />
    ),
    [],
  );

  return (
    <PageLayout title={t("search.searchHistory")} titleIcon="search">
      <Space h="sm" />
      <Stack gap={"md"}>
        <ResultToolbar
          filtersParams={filtersParams}
          setFiltersParams={(newFilters) => handleSetFilterParams(newFilters)}
          onSearch={refetch}
        />

        <SearchingStatusCheck isSearching={isLoading}>
          <SimilarPartsErrorCheck similarParts={parts ?? null}>
            <SearchResult
              similarParts={parts}
              Card={Card}
              columnCount={parseInt(columns ?? DEFAULT_COLUMNS_COUNT)}
            >
              <Group justify="flex-end">
                <SearchResult.MoreButton />
              </Group>
            </SearchResult>
          </SimilarPartsErrorCheck>
        </SearchingStatusCheck>
      </Stack>
    </PageLayout>
  );
};
